import axiosInstance from "@/services/axios/api";
import kernelApi from "@/services/axios/api.kernel";
import tokenRenewalService from "@/services/auth/token-renewal.service";
import { Store } from "@/store";
import toastService from "@/services/globals/toast.service";
import { extrairMensagemErroHttp } from "@/services/helper/erro-http.util";
import erroCampoService from "@/services/globals/erro-campo.service";
import registroErrosService from "@/services/assistente/registro-erros.service";
import diagnosticoTelaService from "@/services/assistente/diagnostico-tela.service";
const { state, actions } = useOidcStore();
const { getters } = useStore();
import { useStore } from "@/store"
import { unref, computed } from "vue";
import { useOidcStore } from "vue3-oidc";
import DadosLocais from '@/services/helper/storage'
const dadosLocais = DadosLocais.getInstance()
const userManager = computed(() => state.value.userManager);

const ROTA_RENOVAR_TOKEN = "/Users/RenovarToken";

// Renova o token (chamado sempre via Kernel API, que é quem emite) e repete a requisição original
// na MESMA instância axios que originalmente falhou (Academic ou Kernel).
const tentarRenovarTokenERepetir = async (instanciaParaRetry: any, originalConfig: any) => {
  const codigoGrupoUsuarioStr = dadosLocais.getCodigoGrupoUsuario();
  const codigoGrupoUsuario = codigoGrupoUsuarioStr ? Number(codigoGrupoUsuarioStr) : null;

  const accessToken = await tokenRenewalService.renovar(codigoGrupoUsuario);

  // Fase 4 (cookie seguro): em produção não há token no corpo (accessToken vem undefined) — o
  // cookie já foi atualizado pela própria renovação (Set-Cookie). Um header "Bearer undefined"
  // aqui teria prioridade sobre o cookie na validação do servidor (header sempre vem antes do
  // fallback de cookie), fazendo o retry falhar de novo à toa mesmo com a sessão já renovada.
  if (accessToken) {
    originalConfig.headers["Authorization"] = `Bearer ${accessToken}`;
  }

  return instanciaParaRetry(originalConfig);
};

const criarResponseErrorHandler = (instanciaParaRetry: any) => async (err: any) => {
  const originalConfig = err.config;
  console.error('Erro em requisição Axios', err);

  if (originalConfig?.url === "/auth/login") {
    return Promise.reject(err);
  }

  // Erros recentes para a Sofia do suporte (só método, caminho, status e mensagem da resposta —
  // nunca corpo da requisição, cabeçalho ou token). O 401 que ainda vai ser renovado e repetido
  // não entra: se a repetição falhar, ela passa por aqui de novo.
  const vaiRenovar =
    err.response?.status === 401 &&
    !originalConfig?._retry &&
    originalConfig?.url !== ROTA_RENOVAR_TOKEN &&
    Boolean(dadosLocais.getCodContrato());
  if (!vaiRenovar) {
    registroErrosService.registrarErroAxios(err);
    diagnosticoTelaService.registrarErroAxios(err);
  }

  if (err.response) {
    // Access Token was expired
    if (
      err.response.status === 401 &&
      !originalConfig._retry &&
      originalConfig.url !== ROTA_RENOVAR_TOKEN &&
      dadosLocais.getCodContrato()
    ) {
      originalConfig._retry = true;

      try {
        return await tentarRenovarTokenERepetir(instanciaParaRetry, originalConfig);
      } catch {
        return Promise.reject(err);
      }
    }
    else if (err.response.status === 403 && !originalConfig._retry) {
      originalConfig._retry = true;
      toastService.alertaErro("Você não tem permissão para acessar este recurso. Se acredita que deveria ter acesso, entre em contato com o administrador do sistema.");
    }
    else if (err.response.status === 400 && !originalConfig._retry) {
      originalConfig._retry = true;

      /*
       * Antes do aviso flutuante, tenta ligar o erro AO CAMPO que o causou.
       *
       * O 400 das duas APIs costuma vir como `ValidationProblemDetails`
       * (`{ errors: { campo: [mensagens] } }`), que identifica a propriedade. Quando isso
       * acontece, `aplicarErrosDeCampo` marca o campo com `aria-invalid`, liga a mensagem
       * por `aria-describedby` e move o foco para o primeiro — que é o que 3.3.1
       * (Identificação de Erro) pede e que um aviso no canto da tela não entrega: ele diz
       * QUE houve erro, não ONDE.
       *
       * O aviso flutuante continua saindo quando NENHUM campo foi encontrado (erro de
       * regra de negócio, erro de tela inteira, nome de propriedade que não casa com o
       * `name` do input). Assim o comportamento antigo permanece como rede de segurança e
       * nada deixa de ser comunicado.
       */
      const camposMarcados = erroCampoService.aplicarErrosDeCampo(err);
      if (camposMarcados === 0) {
        const mensagem = extrairMensagemErroHttp(err);
        toastService.alertaErro(mensagem || 'Não foi possível concluir a operação porque os dados enviados são inválidos. Revise as informações e tente novamente.');
      }
    }
    else if (err.response.status === 404 && !originalConfig._retry) {
      originalConfig._retry = true;
      const mensagem = extrairMensagemErroHttp(err);
      toastService.alertaErro(mensagem || 'O recurso solicitado não foi encontrado. Ele pode ter sido removido ou você pode ter chegado aqui por um link desatualizado.');
    }
    else if (err.response.status === 500 && !originalConfig._retry) {
      originalConfig._retry = true;
      const mensagem = extrairMensagemErroHttp(err);
      toastService.alertaErro(mensagem || 'Ocorreu um erro inesperado no servidor. Tente novamente em alguns instantes; se o problema persistir, entre em contato com o suporte.');
    }
  }
  else {
    toastService.alertaErro('Não foi possível se conectar ao servidor. Verifique sua conexão com a internet e tente novamente.');
  }

  return Promise.reject(err);
};

const setup = async (store: Store) => {
  axiosInstance.interceptors.request.use(
    async (config) => {

      /*
       * O GRUPO SELECIONADO na barra, em toda requisição.
       *
       * O token NÃO serve para isso: a claim GruposUsuario carrega TODOS os grupos do usuário de
       * propósito (a seleção de grupo é conveniência de navegação, não fronteira de segurança).
       * Só o front sabe qual está escolhido — e o servidor precisa saber para decidir se o usuário
       * está operando COMO PROFESSOR, que é o que recorta turmas e estudantes pela alocação.
       *
       * Sem isto, quem é supervisor E professor (ou cujo grupo de supervisão tem acesso às
       * funcionalidades de professor) era tratado como professor em toda tela: a ficha descritiva,
       * o relatório descritivo e o lançamento de notas abriam sem turma nenhuma, com "não foi
       * possível identificar o professor da sessão".
       *
       * Mandar o código pelo cabeçalho é seguro: o servidor só o aceita quando o grupo É DO
       * PRÓPRIO USUÁRIO (ver PerfilProfessorHelper.PeloGrupoSelecionado), então forjá-lo só
       * permite escolher entre os grupos que a pessoa já tem — o mesmo que trocar de grupo na
       * barra.
       */
      const codigoGrupoSelecionado = dadosLocais.getCodigoGrupoUsuario();

      if (codigoGrupoSelecionado) {
        config.headers!['X-Codigo-Grupo-Usuario'] = codigoGrupoSelecionado.toString();
      }

      // Sessão normal: o cookie HttpOnly do token vai automaticamente (withCredentials: true em
      // api.ts), não precisa montar o header. Só monta explicitamente pro caso do token legado
      // (recebido via URL por chamada externa via Proxy.vue) — esse não é o cookie da Kernel API.
      const tokenLegacy = useStore().getters.getTokenLegacy;
      if (tokenLegacy) {
        config.headers!["Authorization"] = 'Bearer ' + tokenLegacy;
      } else {
        // Dois cenários mandam token pelo corpo em vez de cookie, e nenhum dos dois seta o cookie
        // HttpOnly da Kernel API:
        // 1. Dev local: a Academic API roda em localhost, e o cookie (Domain=.consultesaeweb.com.br)
        //    nunca chega lá (ver EhOrigemDesenvolvimentoLocal, Kernel API).
        // 2. Produção, acesso público (ex.: pré-matrícula da Central de Vagas, sem login de
        //    usuário): o token vem de Kernel API `Authenticate/Login` (autenticação de aplicação,
        //    usuariosService.obterAccessTokenAcessoPublico), que só devolve o token no corpo.
        // Em produção, uma sessão de usuário normal NUNCA grava token em localStorage (Fase 4,
        // só cookie — ver comentário em routes/index.ts) — então isto só entra em ação nesses
        // dois cenários, sem risco de conflitar com o cookie de uma sessão normal.
        const token = dadosLocais.getAccessToken();
        if (token) {
          config.headers!["Authorization"] = 'Bearer ' + token;
        }
      }



      /*
      const obraSelected = getters.getObraSelecionada;
      const usuarioIntegracao = getters.getSegUsuarioId;
      const isLegacy = useStore().getters.getIsLegacy;
      const tokenLegacy = useStore().getters.getTokenLegacy;
      const tokenLegacyDeserialized = useStore().getters.getTokenLegacyDeserialized;
      if (obraSelected) {
        if (config.url?.indexOf("?") != -1)
          config.url += `&gesObraSelecionadaId=${obraSelected.gesObraId ?? ''}&${isLegacy ? `isLegacy=true&codigoContrato=${tokenLegacyDeserialized.codigoContrato}&` : ""}segUsuarioLogadoId=${usuarioIntegracao ?? ''}`;
        else
          config.url += `?gesObraSelecionadaId=${obraSelected.gesObraId ?? ''}&${isLegacy ? `isLegacy=true&codigoContrato=${tokenLegacyDeserialized.codigoContrato}&` : ""}segUsuarioLogadoId=${usuarioIntegracao ?? ''}`;
      }

      if (!isLegacy) {
        let user = await userManager.value?.getUser();
        try {

       if (user && user.expired) {
            useStore().dispatch(LoaderActionTypes.ACTION_SHOW);
            user = await userManager.value?.signinSilent();
            actions.value.setUser(user!);
            useStore().dispatch(LoaderActionTypes.ACTION_HIDE);
          }
        }
        catch (e) {
          LoaderActionTypes.ACTION_HIDE
        }

        config.headers!["Authorization"] = 'Bearer ' + user?.access_token;  // for Spring Boot back-end
        //config.headers["x-access-token"] = token; // for Node.js Express back-end
      }
      else
        config.headers!["Authorization"] = 'Bearer ' + tokenLegacy;
      //     const cont= opentelemetry.context.active();
      //  const hr= opentelemetry.propagation.inject(opentelemetry.context.active(),{})!;
      //       config.headers = hr;

      */
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  axiosInstance.interceptors.response.use(
    (res) => {
      diagnosticoTelaService.registrarRespostaAxios(res);
      // const span = (spanPool[res.config.headers!['parentSpanId'] as string]) as Span;
      // delete spanPool[res.config.headers!['parentSpanId'] as string];
      // span.end();

      return res;
    },
    criarResponseErrorHandler(axiosInstance)
  );

  // Mesmo tratamento de 401 (renovação silenciosa) na Kernel API — ambas validam o mesmo token.
  kernelApi.interceptors.response.use(
    (res) => {
      diagnosticoTelaService.registrarRespostaAxios(res);
      return res;
    },
    criarResponseErrorHandler(kernelApi)
  );
  // A medição ocorre nas duas APIs e não acessa os cabeçalhos de autenticação.
  for (const instancia of [axiosInstance, kernelApi]) {
    instancia.interceptors.request.use((config) => {
      diagnosticoTelaService.registrarInicioAxios(config);
      return config;
    });
  }
};
export default setup;
